import { type ServiceParams, unwrapJson } from "../client";
import type {
  TenantListResponse,
  TenantProvisionPayload,
  TenantProvisionResponse,
  TenantResponse,
  TenantTrialExtendPayload,
  TenantTrialExtendResponse,
  TenantUpdatePayload,
} from "./types";

/**
 * Tenant service — wraps the tenant-admin self-service endpoints
 * under ``/v1/admin/tenants/me``. Reads + writes both gated to the
 * caller's own tenant; the path-less ``/me`` route guarantees the
 * principal cannot address another tenant from this surface.
 */
const Tenants = ({ url, config }: ServiceParams) => {
  const base = `${url}/v1/admin/tenants`;

  return {
    async getCurrent(): Promise<TenantResponse> {
      const response = await fetch(`${base}/me`, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<TenantResponse>(response);
    },

    async updateCurrent(
      payload: TenantUpdatePayload
    ): Promise<TenantResponse> {
      const response = await fetch(`${base}/me`, {
        method: "PATCH",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<TenantResponse>(response);
    },

    // ------------------------------------------------------------------
    // Super-admin only (Sesi v0.42.0). Backend gate rejects 403 if the
    // caller isn't super_admin — the FE additionally hides every
    // surface that lands here behind a role check.
    // ------------------------------------------------------------------

    async listAll(): Promise<TenantListResponse> {
      const response = await fetch(base, {
        method: "GET",
        headers: config(),
      });
      return unwrapJson<TenantListResponse>(response);
    },

    async provision(
      payload: TenantProvisionPayload
    ): Promise<TenantProvisionResponse> {
      const response = await fetch(base, {
        method: "POST",
        headers: config(),
        body: JSON.stringify(payload),
      });
      return unwrapJson<TenantProvisionResponse>(response);
    },

    async extendTrial(
      tenantId: string,
      payload: TenantTrialExtendPayload
    ): Promise<TenantTrialExtendResponse> {
      const response = await fetch(
        `${base}/${tenantId}/extend-trial`,
        {
          method: "PATCH",
          headers: config(),
          body: JSON.stringify(payload),
        }
      );
      return unwrapJson<TenantTrialExtendResponse>(response);
    },
  };
};

export default Tenants;
